import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { formatDate } from 'sharedUi/dateUtils';
import { promptsService } from '../../services/prompts.service';
import type { PromptChangeLogEntry, PromptVersion, PromptVersionStatus } from '../../types';
import { ActionButton, StatusChip } from '../common/panel';
import { Icon } from '../common/icon';
import type { Tone } from '../common/ui-kit';
import { extractErrorMessage } from '../../utils/error-handler';

const STATUS: Record<PromptVersionStatus, { label: string; tone: Tone }> = {
  ACTIVE: { label: 'Active version', tone: 'success' },
  DRAFT: { label: 'Draft', tone: 'warn' },
  ARCHIVED: { label: 'Archived', tone: 'neutral' },
  DISCARDED: { label: 'Discarded', tone: 'neutral' },
};

const ACTION: Record<PromptChangeLogEntry['action'], string> = {
  CREATED: 'Created',
  BASELINE: 'Recorded existing text',
  DRAFT_SAVED: 'Started a draft',
  DRAFT_UPDATED: 'Edited the draft',
  DRAFT_DISCARDED: 'Discarded the draft',
  ACTIVATED: 'Made active',
  DETAILS_UPDATED: 'Changed details',
  DELETED: 'Deleted',
  RESTORED: 'Restored',
  PROJECT_CHANGED: 'Moved project',
  TAGGED: 'Moved tags',
};

const FIELD: Record<string, string> = {
  aiRole: 'AI role', systemRole: 'System role', objective: 'Objective', taskInstructions: 'Task instructions',
  taskInput: 'Task input', taskOutputFormat: 'Output format', taskExample: 'Task example', llm: 'Model label',
  modelId: 'Model', settingsJson: 'Settings', name: 'Name', type: 'Type', iconPath: 'Icon', tech: 'Technology',
  evalIds: 'Evals', appId: 'Project',
};

const fieldLabel = (f: string) => (f.startsWith('tag:') ? `Tag “${f.slice(4)}”` : FIELD[f] || f);

const show = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'string' ? v : JSON.stringify(v);
};

type Pending = { kind: 'publish' | 'rollback' | 'discard'; version: number } | null;

/**
 * A prompt's versions — which one is live, which is being drafted, which came
 * before — and its change log. The live prompt is always the ACTIVE version;
 * a draft is edited from the Edit screen and published or discarded here.
 */
const PromptVersionsPanel = ({ promptId, canUpdate, onChanged, onRunDraft }: {
  promptId: string;
  canUpdate: boolean;
  /** The live prompt changed (publish / roll back / discard) — reload it. */
  onChanged: () => void;
  onRunDraft?: (version: number) => void;
}) => {
  const qc = useQueryClient();
  const [pending, setPending] = useState<Pending>(null);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [showAllLog, setShowAllLog] = useState(false);
  const [tagging, setTagging] = useState<{ version: number; text: string } | null>(null);

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['prompt-versions', promptId],
    queryFn: () => promptsService.listVersions(promptId),
  });
  const { data: log = [] } = useQuery({
    queryKey: ['prompt-change-log', promptId],
    queryFn: () => promptsService.getChangeLog(promptId),
  });

  const act = useMutation({
    mutationFn: async (p: NonNullable<Pending>) => (p.kind === 'discard'
      ? promptsService.discardDraft(promptId, note.trim() || undefined)
      : promptsService.activateVersion(promptId, p.version, note.trim() || undefined)),
    onSuccess: () => {
      setPending(null); setNote('');
      qc.invalidateQueries({ queryKey: ['prompt-versions', promptId] });
      qc.invalidateQueries({ queryKey: ['prompt-change-log', promptId] });
      onChanged();
    },
  });

  const saveTags = useMutation({
    mutationFn: (t: { version: number; text: string }) => promptsService.setVersionTags(
      promptId, t.version, t.text.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)),
    onSuccess: () => {
      setTagging(null);
      qc.invalidateQueries({ queryKey: ['prompt-versions', promptId] });
      qc.invalidateQueries({ queryKey: ['prompt-change-log', promptId] });
      onChanged();
    },
  });

  const title = pending?.kind === 'publish' ? `Publish draft v${pending.version}?`
    : pending?.kind === 'rollback' ? `Roll back to v${pending?.version}?` : `Discard draft v${pending?.version}?`;
  const explain = pending?.kind === 'publish'
    ? 'It becomes the active version: every run, eval and RAG answer uses it from now on. The current version is archived and can be rolled back to.'
    : pending?.kind === 'rollback'
      ? 'This archived version becomes active again. The current active version is archived.'
      : 'The draft is kept in the change log but can no longer be published. The live prompt is not affected.';

  return (
    <div className="ui-panel p-5" data-testid="PromptView.Versions">
      <header className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold text-ink">Versions</h2>
        <p className="text-xs text-ink-muted">Every version is kept. Only the active one runs.</p>
      </header>

      {isLoading ? <p className="mt-3 text-sm text-ink-muted">Loading…</p> : (
        <ol className="pv-list mt-3">
          {versions.map((v: PromptVersion) => {
            const isOpen = open === v.version;
            const date = v.status === 'ACTIVE' && v.activatedAt
              ? `Active since ${formatDate(v.activatedAt)}` : formatDate(v.updatedAt || v.createdAt || '');
            return (
              <li key={v._id} className="pv-item" data-testid={`PromptVersion.v${v.version}`}>
                <div className="flex items-start gap-3">
                  <button type="button" onClick={() => setOpen(isOpen ? null : v.version)} aria-expanded={isOpen}
                    className="ui-press pv-toggle" aria-label={`${isOpen ? 'Hide' : 'Show'} v${v.version} details`}>
                    <Icon name="chevron-right" size={14} className={`pv-chevron ${isOpen ? 'pv-chevron--open' : ''}`} />
                  </button>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                      <span className="text-sm font-semibold tabular-nums text-ink">v{v.version}</span>
                      <StatusChip tone={STATUS[v.status].tone}>{STATUS[v.status].label}</StatusChip>
                      {(v.tags || []).map((t) => <StatusChip key={t} tone="info" dot={false}>{t}</StatusChip>)}
                      <span className="ml-auto text-xs tabular-nums text-ink-muted whitespace-nowrap">{date}</span>
                    </div>
                    {v.changeNote && <p className="pv-note">{v.changeNote}</p>}

                    {canUpdate && v.status !== 'DISCARDED' && (
                      <div className="mt-2 flex flex-wrap items-center gap-1">
                        {v.status === 'DRAFT' && <ActionButton size="sm" icon="check" label="Publish" kind="primary" onClick={() => setPending({ kind: 'publish', version: v.version })} testId="PromptVersion.Publish" />}
                        {v.status === 'DRAFT' && onRunDraft && <ActionButton size="sm" icon="play" label="Test run" onClick={() => onRunDraft(v.version)} testId="PromptVersion.RunDraft" />}
                        {v.status === 'ARCHIVED' && <ActionButton size="sm" icon="history" label="Roll back to this" onClick={() => setPending({ kind: 'rollback', version: v.version })} testId="PromptVersion.Rollback" />}
                        <ActionButton size="sm" kind="quiet" icon="tag" label="Tags" onClick={() => { saveTags.reset(); setTagging({ version: v.version, text: (v.tags || []).join(', ') }); }} testId="PromptVersion.Tags" />
                        {v.status === 'DRAFT' && <span className="ml-auto"><ActionButton size="sm" icon="x" label="Discard" kind="danger" onClick={() => setPending({ kind: 'discard', version: v.version })} testId="PromptVersion.Discard" /></span>}
                      </div>
                    )}

                    {isOpen && (
                      <dl className="ui-inset pv-detail mt-3">
                        <dt>Key</dt><dd className="font-mono text-xs">{v.versionKey}</dd>
                        {Object.entries(v.content || {}).filter(([, val]) => val !== null && val !== undefined && val !== '').map(([k, val]) => (
                          <div key={k} className="contents">
                            <dt>{FIELD[k] || k}</dt>
                            <dd>{show(val)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <header className="mt-7 flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold text-ink">Change log</h2>
        <p className="text-xs tabular-nums text-ink-muted">{log.length} change{log.length === 1 ? '' : 's'}</p>
      </header>
      <ol className="pv-log mt-4" data-testid="PromptView.ChangeLog">
        {(showAllLog ? log : log.slice(0, 5)).map((e) => (
          <li key={e._id} className="pv-log__item">
            {/* Gutter: version over date, right-aligned against the rail. */}
            <div className="pv-log__when">
              {e.version != null && <span className="pv-log__ver">v{e.version}</span>}
              <time className="pv-log__date">{formatDate(e.at)}</time>
            </div>
            <div className="pv-log__body">
              <span className="pv-log__dot" aria-hidden />
              <h3 className="pv-log__title">{ACTION[e.action] || e.action}</h3>
              {e.note && <p className="pv-log__note">{e.note}</p>}
              {e.changes.length > 0 && (
                <ul className="ui-inset pv-diff mt-3">
                  {e.changes.map((c, i) => (
                    <li key={i}>
                      <span className="font-medium text-ink">{fieldLabel(c.field)}</span>
                      <span className="pv-diff__from">{show(c.from)}</span>
                      <span aria-hidden>→</span>
                      <span className="text-ink">{show(c.to)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
      {log.length > 5 && (
        <div className="pv-log__more">
          <ActionButton size="sm" kind="quiet" icon={showAllLog ? 'chevron-right' : 'chevron-down'} label={showAllLog ? 'Show less' : `Show all ${log.length}`} onClick={() => setShowAllLog(!showAllLog)} />
        </div>
      )}

      <Dialog header={`Tags on v${tagging?.version}`} visible={!!tagging} onHide={() => setTagging(null)}
        style={{ width: '28rem', maxWidth: '95vw' }} dismissableMask data-testid="PromptVersion.TagDialog">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Apps, jobs and eval suites can run a prompt by tag (for example <code>prod</code>). A tag names one
          version: adding it here takes it off any other version. <code>active</code> and <code>draft</code> are built in.
        </p>
        <label className="mt-3 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="versionTags">Tags, comma-separated</label>
        <InputText id="versionTags" value={tagging?.text || ''} className="mt-1 w-full" placeholder="prod, stable"
          onChange={(e) => tagging && setTagging({ ...tagging, text: e.target.value })} />
        {saveTags.isError && <Message severity="error" className="mt-2 w-full" text={extractErrorMessage(saveTags.error, 'Could not save the tags.')} />}
        <div className="mt-4 flex justify-end gap-2">
          <ActionButton kind="quiet" label="Cancel" onClick={() => setTagging(null)} />
          <ActionButton kind="primary" label="Save tags" loading={saveTags.isPending} onClick={() => tagging && saveTags.mutate(tagging)}
            testId="PromptVersion.TagDialog.Save" />
        </div>
      </Dialog>

      <Dialog header={title} visible={!!pending} onHide={() => { setPending(null); act.reset(); }}
        style={{ width: '30rem', maxWidth: '95vw' }} dismissableMask data-testid="PromptVersion.Confirm">
        <p className="text-sm text-gray-700 dark:text-gray-300">{explain}</p>
        <label className="mt-3 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="versionNote">
          Note for the change log {pending?.kind === 'discard' ? '(optional)' : ''}
        </label>
        <InputTextarea id="versionNote" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 w-full"
          placeholder={pending?.kind === 'rollback' ? 'Why roll back?' : pending?.kind === 'publish' ? 'What changes for users?' : 'Why discard it?'} />
        {act.isError && <Message severity="error" className="mt-2 w-full" text={extractErrorMessage(act.error, 'That did not work.')} />}
        <div className="mt-4 flex justify-end gap-2">
          <ActionButton kind="quiet" label="Cancel" onClick={() => { setPending(null); act.reset(); }} />
          <ActionButton kind={pending?.kind === 'discard' ? 'danger' : 'primary'} loading={act.isPending}
            label={pending?.kind === 'publish' ? 'Publish' : pending?.kind === 'rollback' ? 'Roll back' : 'Discard draft'}
            onClick={() => pending && act.mutate(pending)} testId="PromptVersion.Confirm.Go" />
        </div>
      </Dialog>
    </div>
  );
};

export default PromptVersionsPanel;
